import type { EChartsOption } from 'echarts';
import type { BibliotecaGrafico, BibliotecaResponse } from '../models/biblioteca.models';

const BLUE_PALETTE = [
  '#CED6EF', '#B5C2E6', '#9DADDE', '#8499D6', '#788ED2', '#546FC6',
  '#5483C7', '#5498C7', '#55ACC7', '#54BFC7', '#53C7BA', '#54C7A6',
  '#55C793', '#6ACFA2', '#83D7B1', '#9CDFC1', '#B5E7D0', '#CDEFE0',
];

function evenlySpacedArray<T>(array: T[], amount: number): T[] {
  const jumps = Math.ceil(array.length / amount);
  const result: T[] = [];
  for (let i = 0; i < array.length; i += jumps) {
    result.push(array[i]);
  }
  return result;
}

function applyBluePalette(chartData: EChartsOption): void {
  if (Array.isArray(chartData.series)) {
    chartData.color = evenlySpacedArray(BLUE_PALETTE, chartData.series.length);
  }
}

/** Extrai `jsonData.title.text` — suporta title como objeto ou array. */
export function extractEchartsTitleText(
  title: EChartsOption['title'] | undefined,
): string {
  if (!title) return '';
  if (Array.isArray(title)) return title[0]?.text?.toString() ?? '';
  return title.text?.toString() ?? '';
}

type ChartJsonData = EChartsOption & { _chartTitle?: string };

function stashChartTitle(jsonData: ChartJsonData): void {
  const titleText =
    jsonData._chartTitle ?? extractEchartsTitleText(jsonData.title);
  if (titleText) jsonData._chartTitle = titleText;
  jsonData.title = { show: false };
}

/**
 * Título editorial de um gráfico da biblioteca.
 * Usa `graficosAgrupados[i][i]` com fallback para `graficosAgrupados[i][0]`.
 */
export function getBibliotecaChartTitle(
  grupos: BibliotecaGrafico[][] | undefined,
  index: number,
): string {
  const grupo = grupos?.[index];
  if (!grupo) return '';
  const grafico = grupo[index] ?? grupo[0];
  const jsonData = grafico?.jsonData as ChartJsonData | undefined;
  if (jsonData?._chartTitle) return jsonData._chartTitle;
  return extractEchartsTitleText(jsonData?.title);
}

/** Título de gráfico em `graficos[]` (array plano — transição energia, brasil solar). */
export function getBibliotecaGraficoTitle(
  graficos: BibliotecaGrafico[] | undefined,
  index: number,
): string {
  return extractEchartsTitleText(graficos?.[index]?.jsonData?.title);
}

/** Só traduz chaves i18n (`pages.*`, `DS_*`); textos legíveis da API são exibidos como estão. */
export function isBibliotecaTitleI18nKey(value: string): boolean {
  return value.startsWith('pages.') || /^DS_[A-Z0-9_]+$/.test(value);
}

export function resolveBibliotecaChartTitle(
  title: string,
  translate: (key: string) => string,
  scope?: string,
): string {
  if (!title) return '';
  if (!isBibliotecaTitleI18nKey(title)) return title;
  const key = title.startsWith('pages.') ? title : scope ? `${scope}.${title}` : title;
  const translated = translate(key);
  return translated === key ? title : translated;
}

/**
 * Normaliza opções ECharts vindas da API — espelha `ChartUtil.transformEchartsDS` do front-17.
 * Stories com id 26 (gênero) são ignorados nesta etapa; formatters específicos tratam os gráficos.
 */
export function transformEchartsDS(
  chartData: BibliotecaResponse,
  language: 'pt' | 'en',
): BibliotecaResponse {
  const unitSuffixes = [
    ' (em milhões)', ' (in millions)', ' (em milhares)', ' (in thousands)',
    ' (em bilhões)', ' (in billions)',
  ];

  const axisLabelY = {
    type: 'value' as const,
    nameGap: 50,
    nameLocation: 'middle' as const,
    name: '',
    axisLabel: {
      formatter(value: number) {
        let returnValue = value;
        if (value >= 1_000_000_000) returnValue = value / 1_000_000_000;
        else if (value >= 1_000_000) returnValue = value / 1_000_000;
        else if (value >= 1_000) returnValue = value / 1_000;
        return new Intl.NumberFormat(language).format(returnValue);
      },
    },
  };

  const legendFormat = (value: number) =>
    new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(value);

  if (!chartData.graficosAgrupados) return chartData;

  for (let index = 0; index < chartData.graficosAgrupados.length; index++) {
    for (let indexGrp = 0; indexGrp < chartData.graficosAgrupados[index].length; indexGrp++) {
      if (
        chartData.id === 26 ||
        (chartData.id === 23 &&
          (index === 4 || (index === 3 && indexGrp === 1)))
      ) {
        continue;
      }

      const grafico = chartData.graficosAgrupados[index][indexGrp].jsonData as EChartsOption & {
        tooltip?: { valueFormatter?: (value: number) => string; order?: string };
        series?: Array<{ markLine?: unknown; data?: number[]; type?: string }>;
        yAxis?: { name?: string };
      };
      if (!grafico) continue;

      if (grafico.tooltip) {
        grafico.tooltip.valueFormatter = legendFormat;
        grafico.tooltip.order = 'valueDesc';
      }

      const series = Array.isArray(grafico.series) ? grafico.series : null;
      if (
        series &&
        series[0]?.type !== 'sankey' &&
        (series[0]?.type === 'line' || series[0]?.type === 'bar')
      ) {
        const inBillions = series.some(
          (r) => !r.markLine && (r.data ?? []).some((d) => d > 1_000_000_000),
        );
        const inMillions = series.some(
          (r) => !r.markLine && (r.data ?? []).some((d) => d > 1_000_000),
        );
        const inThousands = series.some(
          (r) => !r.markLine && (r.data ?? []).some((d) => d > 1_000),
        );

        const yAxis = Array.isArray(grafico.yAxis) ? grafico.yAxis[0] : grafico.yAxis;
        if (yAxis?.name || inMillions || inThousands || inBillions) {
          axisLabelY.name = yAxis?.name ?? '';

          if (!unitSuffixes.some((suffix) => axisLabelY.name.includes(suffix))) {
            if (inBillions) {
              axisLabelY.name += language === 'pt' ? ' (em bilhões)' : ' (in billions)';
            } else if (inMillions && !inBillions) {
              axisLabelY.name += language === 'pt' ? ' (em milhões)' : ' (in millions)';
            } else if (inThousands && !inMillions && !inBillions) {
              axisLabelY.name += language === 'pt' ? ' (em milhares)' : ' (in thousands)';
            }
          }
        }
        grafico.yAxis = { ...axisLabelY };
      }
    }
  }

  return chartData;
}

/** Aplica paleta editorial e oculta títulos embutidos — espelha `regbrBibliotecaPipe` do front-17. */
export function prepareBibliotecaCharts(
  data: BibliotecaResponse,
  language: 'pt' | 'en',
): BibliotecaResponse {
  const prepared = transformEchartsDS(data, language);

  if (!prepared.graficosAgrupados) return prepared;

  prepared.graficosAgrupados = prepared.graficosAgrupados.map((graficos) =>
    graficos.map((grafico: BibliotecaGrafico) => {
      applyBluePalette(grafico.jsonData);
      stashChartTitle(grafico.jsonData as ChartJsonData);
      return grafico;
    }),
  );

  return prepared;
}

/** Oculta título embutido do ECharts — o `chart-card` exibe o título no header. */
export function suppressEchartsTitle(options: EChartsOption): EChartsOption {
  const { title, _chartTitle, ...rest } = options as EChartsOption & {
    _chartTitle?: string;
  };
  return rest;
}

/** Paleta editorial para `graficos[]` plano — espelha `prepareBibliotecaCharts` para agrupados. */
export function prepareBibliotecaGraficosList(
  data: BibliotecaResponse,
  language: 'pt' | 'en',
): BibliotecaResponse {
  const prepared = transformEchartsDS(data, language);
  if (!prepared.graficos) return prepared;

  prepared.graficos = prepared.graficos.map((grafico: BibliotecaGrafico) => {
    applyBluePalette(grafico.jsonData);
    stashChartTitle(grafico.jsonData as ChartJsonData);
    return grafico;
  });

  return prepared;
}
